<div class="builder-page">

  <!-- ═══ STICKY SET HEADER ═══ -->
  <mat-card class="set-header-card" appearance="outlined">
    <div class="card-banner set-banner">
      <mat-icon class="banner-icon">playlist_add_check</mat-icon>

      <div class="banner-text">
        <h2>{{ setName || ('MESSAGE_SET_BUILDER.UNTITLED' | translate) }}</h2>
        <div class="set-meta">
          <span class="meta-chip" [class.private]="isSetPrivate">
            <mat-icon>{{ isSetPrivate ? 'lock' : 'public' }}</mat-icon>
            {{ (isSetPrivate ? 'MESSAGE_SET_BUILDER.PRIVATE' : 'MESSAGE_SET_BUILDER.PUBLIC') | translate }}
          </span>
          <span class="meta-chip">
            <mat-icon>forum</mat-icon>
            {{ messageCount }}
            {{ (messageCount === 1
                  ? 'MESSAGE_SET_BUILDER.MESSAGE_IN_SET'
                  : 'MESSAGE_SET_BUILDER.MESSAGES_IN_SET') | translate }}
          </span>
          <span class="meta-chip" *ngIf="setTag">
            <mat-icon>sell</mat-icon>
            {{ setTag }}
          </span>
        </div>
      </div>

      <div class="header-actions">
        <!-- span wrapper: a disabled button emits no pointer events, so the
             tooltip would never fire on the control itself -->
        <span
          class="save-wrapper"
          [matTooltip]="(saveBlockedReason() | translate) || ''"
          [matTooltipDisabled]="!saveBlockedReason()"
        >
          <button mat-flat-button color="primary" (click)="saveMessageSet()" [disabled]="isSaveDisabled()">
            <mat-icon>save</mat-icon>
            {{ 'MESSAGE_SET_BUILDER.SAVE' | translate }}
          </button>
        </span>
        <button mat-stroked-button (click)="showMessageSetPreviewModal()" [disabled]="!messageCount">
          <mat-icon>visibility</mat-icon>
          {{ 'MESSAGE_SET_BUILDER.PREVIEW' | translate }}
        </button>
        <button mat-stroked-button (click)="onCancel()">
          <mat-icon>arrow_back</mat-icon>
          {{ 'MESSAGE_SET_BUILDER.BACK' | translate }}
        </button>
      </div>
    </div>

    <!-- Why Save is unavailable, with the action that resolves it -->
    <div class="save-blocked-hint" *ngIf="saveBlockedReason() as reason">
      <mat-icon>warning_amber</mat-icon>
      <span>{{ reason | translate }}</span>
      <button mat-stroked-button *ngIf="!isAddDisabled()" (click)="addMessageToSet()">
        {{ (isCurrentMessageInMessageSet()
              ? 'MESSAGE_SET_BUILDER.UPDATE_MESSAGE'
              : 'MESSAGE_SET_BUILDER.ADD_TO_SET') | translate }}
      </button>
      <button mat-stroked-button *ngIf="currentMessageForm.dirty" (click)="discardDraft()">
        {{ 'MESSAGE_SET_BUILDER.DISCARD_DRAFT' | translate }}
      </button>
    </div>

    <mat-divider></mat-divider>

    <!-- ── Set Details (collapsed by default; the summary above keeps the
         set's identity on screen while you work on messages) ── -->
    <mat-expansion-panel class="set-details-panel" [(expanded)]="setDetailsExpanded">
      <mat-expansion-panel-header>
        <mat-panel-title>
          <mat-icon class="section-icon">description</mat-icon>
          <span>{{ 'MESSAGE_SET_BUILDER.SET_DETAILS' | translate }}</span>
        </mat-panel-title>
      </mat-expansion-panel-header>

      <form [formGroup]="messageSetForm">
        <div class="form-row">
          <mat-form-field appearance="outline" class="flex-1">
            <mat-label>{{ 'MESSAGE_SET_BUILDER.NAME' | translate }}</mat-label>
            <input matInput formControlName="name" placeholder="{{ 'MESSAGE_SET_BUILDER.NAME_PLACEHOLDER' | translate }}" required />
            <mat-error *ngIf="messageSetForm.get('name')?.hasError('required')">
              {{ 'MESSAGE_SET_BUILDER.NAME_REQUIRED' | translate }}
            </mat-error>
          </mat-form-field>

          <mat-form-field appearance="outline" class="flex-1">
            <mat-label>{{ 'MESSAGE_SET_BUILDER.DESCRIPTION' | translate }}</mat-label>
            <input matInput formControlName="description" placeholder="{{ 'MESSAGE_SET_BUILDER.DESCRIPTION_PLACEHOLDER' | translate }}" />
          </mat-form-field>
        </div>

        <div class="form-row">
          <mat-form-field appearance="outline" class="flex-1">
            <mat-label>{{ 'MESSAGE_SET_BUILDER.TAG' | translate }}</mat-label>
            <input matInput formControlName="tag" placeholder="{{ 'MESSAGE_SET_BUILDER.TAG_PLACEHOLDER' | translate }}" />
            <mat-icon matSuffix matTooltip="{{ 'MESSAGE_SET_BUILDER.TAG_TOOLTIP' | translate }}">help_outline</mat-icon>
          </mat-form-field>

          <div class="visibility-field flex-1" *ngIf="showVisibility && isVisibilityEnabled">
            <mat-label>{{ 'MESSAGE_SET_BUILDER.VISIBILITY' | translate }}</mat-label>
            <mat-slide-toggle formControlName="is_private" color="primary">
              <span *ngIf="messageSetForm.get('is_private')?.value">{{ 'MESSAGE_SET_BUILDER.PRIVATE' | translate }}</span>
              <span *ngIf="!messageSetForm.get('is_private')?.value">{{ 'MESSAGE_SET_BUILDER.PUBLIC' | translate }}</span>
            </mat-slide-toggle>
            <mat-icon class="visibility-help" matTooltip="{{ 'MESSAGE_SET_BUILDER.VISIBILITY_TOOLTIP' | translate }}">help_outline</mat-icon>
          </div>
        </div>

        <div class="form-row" *ngIf="showOwnerSelect && !messageSetForm.get('is_private')?.value">
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>{{ 'MESSAGE_SET_BUILDER.OWNER' | translate }}</mat-label>
            <mat-select formControlName="owner">
              <mat-option *ngFor="let admin of customerAdmins" [value]="admin.id">
                {{ admin.last_name }}, {{ admin.first_name }}
              </mat-option>
            </mat-select>
          </mat-form-field>
        </div>
      </form>
    </mat-expansion-panel>
  </mat-card>

  <div class="builder-columns">

    <!-- ═══ LEFT: Message list ═══ -->
    <mat-card class="list-card" appearance="outlined">
      <div class="card-banner list-banner">
        <mat-icon class="banner-icon">reorder</mat-icon>
        <div class="banner-text list-banner-text">
          <h2>{{ 'MESSAGE_SET_BUILDER.MESSAGES_SECTION' | translate }}</h2>
          <span class="count-pill">{{ messageCount }}</span>
        </div>
        <button
          mat-icon-button
          class="collapse-toggle"
          (click)="toggleMessageList()"
          *ngIf="messageCount"
          [attr.aria-label]="'MESSAGE_SET_BUILDER.MESSAGES_SECTION' | translate"
        >
          <mat-icon>{{ isMessageListCollapsed ? 'expand_more' : 'expand_less' }}</mat-icon>
        </button>
      </div>

      <mat-divider></mat-divider>

      <mat-card-content [class.collapsed]="isMessageListCollapsed">
        <div class="message-list" cdkDropList (cdkDropListDropped)="onMessageDrop($event)">
          <!-- Rows are summaries only.  The editor on the right is the single
               detail view; rendering the selected message here as well put the
               same content on screen twice. -->
          <div *ngFor="let message of workingSet?.messages; let i = index"
               cdkDrag
               class="message-item"
               [class.selected]="selectedMessageIndex === i"
               (click)="selectMessage(i)">

            <mat-icon class="drag-handle" cdkDragHandle>drag_indicator</mat-icon>
            <span class="message-index">{{ message.index }}</span>

            <div class="message-summary">
              <span class="message-title">{{ messageTitle(message) }}</span>
              <span class="message-preview" *ngIf="message.subject">{{ message.body }}</span>
              <div class="message-meta">
                <span class="meta-tag" *ngIf="choiceCount(message)">
                  <mat-icon>quiz</mat-icon>{{ choiceCount(message) }}
                </span>
                <span class="meta-tag" *ngIf="mediaCount(message)">
                  <mat-icon>{{ hasVideo(message) ? 'videocam' : 'image' }}</mat-icon>{{ mediaCount(message) }}
                </span>
                <span class="meta-tag" *ngIf="message.tag">
                  <mat-icon>sell</mat-icon>{{ message.tag }}
                </span>
              </div>
            </div>

            <button
              mat-icon-button
              class="remove-btn"
              (click)="removeMessage(i); $event.stopPropagation()"
              [attr.aria-label]="'MESSAGE_SET_BUILDER.REMOVE_MESSAGE' | translate"
            >
              <mat-icon>close</mat-icon>
            </button>
          </div>

          <div class="empty-state" *ngIf="!messageCount">
            <div class="empty-icon-wrapper">
              <mat-icon>drafts</mat-icon>
            </div>
            <h3>{{ 'MESSAGE_SET_BUILDER.NO_MESSAGES_YET' | translate }}</h3>
            <p>{{ 'MESSAGE_SET_BUILDER.NO_MESSAGES_HINT' | translate }}</p>
          </div>
        </div>
      </mat-card-content>
    </mat-card>

    <!-- ═══ RIGHT: Editor ═══ -->
    <mat-card class="editor-card" appearance="outlined">
      <div class="card-banner editor-banner">
        <mat-icon class="banner-icon">edit_note</mat-icon>
        <div class="banner-text">
          <h2>{{ 'MESSAGE_SET_BUILDER.EDITOR_TITLE' | translate }}</h2>
          <!-- Ties the two panels together without repeating their content -->
          <span *ngIf="selectedMessageIndex > -1">
            {{ 'MESSAGE_SET_BUILDER.EDITING_MESSAGE' | translate: {
                 index: selectedMessageIndex + 1, total: messageCount } }}
          </span>
          <span *ngIf="selectedMessageIndex === -1">
            {{ 'MESSAGE_SET_BUILDER.NEW_MESSAGE_DRAFT' | translate }}
          </span>
        </div>
      </div>

      <mat-divider></mat-divider>

      <mat-card-content>
        <form [formGroup]="currentMessageForm">
          <!-- ── Compose Message ── -->
          <div class="form-section">
            <div class="section-label">
              <mat-icon class="section-icon">create</mat-icon>
              <span>{{ 'MESSAGE_SET_BUILDER.COMPOSE_MESSAGE' | translate }}</span>
            </div>

            <div class="media-toolbar">
              <button mat-stroked-button type="button" (click)="showMediaLibraryModal()">
                <mat-icon>photo_camera</mat-icon>
                {{ 'MESSAGE_SET_BUILDER.ATTACH_MEDIA' | translate }}
              </button>
              <button mat-stroked-button type="button" (click)="clearMediaSelection()" *ngIf="currentMessageHasMedia()">
                <mat-icon>clear</mat-icon>
                {{ 'MESSAGE_SET_BUILDER.CLEAR_MEDIA' | translate }}
              </button>
            </div>

            <div class="selected-media" *ngIf="currentMessageHasMedia()">
              <cm-image-thumbnails *ngIf="currentMessage?.images?.length" [images]="currentMessage.images"></cm-image-thumbnails>
              <cm-video-thumbnails *ngIf="currentMessage?.videos?.length" [videos]="currentMessage.videos"></cm-video-thumbnails>
            </div>

            <div class="form-row">
              <mat-form-field appearance="outline" class="flex-1">
                <mat-label>{{ 'MESSAGE_SET_BUILDER.SUBJECT' | translate }}</mat-label>
                <input matInput formControlName="subject"
                       placeholder="{{ 'MESSAGE_SET_BUILDER.SUBJECT_PLACEHOLDER' | translate }}"
                       [maxlength]="getSubjectMaxLength()" />
              </mat-form-field>

              <mat-form-field appearance="outline" class="flex-1">
                <mat-label>{{ 'MESSAGE_SET_BUILDER.MESSAGE_TAG' | translate }}</mat-label>
                <input matInput formControlName="tag" placeholder="{{ 'MESSAGE_SET_BUILDER.MESSAGE_TAG_PLACEHOLDER' | translate }}" />
                <mat-icon matSuffix matTooltip="{{ 'MESSAGE_SET_BUILDER.MESSAGE_TAG_TOOLTIP' | translate }}">help_outline</mat-icon>
              </mat-form-field>
            </div>

            <div class="char-progress" *ngIf="currentMessageForm.get('subject')?.value">
              <div class="char-bar">
                <div class="char-fill"
                     [style.width.%]="getSubjectPercentage()"
                     [class.warn]="getSubjectRemaining() <= 10"
                     [class.danger]="getSubjectRemaining() <= 5"></div>
              </div>
              <span class="char-count" [class.warn]="getSubjectRemaining() <= 10">
                {{ getSubjectRemaining() }} {{ 'MESSAGE_SET_BUILDER.REMAINING' | translate }}
              </span>
            </div>

            <mat-form-field appearance="outline" class="full-width">
              <mat-label>{{ 'MESSAGE_SET_BUILDER.BODY' | translate }}</mat-label>
              <textarea matInput formControlName="body"
                        placeholder="{{ 'MESSAGE_SET_BUILDER.BODY_PLACEHOLDER' | translate }}"
                        rows="4"
                        [maxlength]="getBodyMaxLength()"
                        required></textarea>
              <mat-error *ngIf="currentMessageForm.get('body')?.hasError('required')">
                {{ 'MESSAGE_SET_BUILDER.BODY_REQUIRED' | translate }}
              </mat-error>
            </mat-form-field>

            <div class="char-progress" *ngIf="currentMessageForm.get('body')?.value">
              <div class="char-bar">
                <div class="char-fill"
                     [style.width.%]="getBodyPercentage()"
                     [class.warn]="getBodyRemaining() <= 30"
                     [class.danger]="getBodyRemaining() <= 10"></div>
              </div>
              <span class="char-count" [class.warn]="getBodyRemaining() <= 30">
                {{ getBodyRemaining() }} {{ 'MESSAGE_SET_BUILDER.REMAINING' | translate }}
              </span>
            </div>
          </div>

          <mat-divider></mat-divider>

          <!-- ── Response Choices ── -->
          <div class="form-section">
            <div class="section-label">
              <mat-icon class="section-icon">quiz</mat-icon>
              <span>{{ 'MESSAGE_SET_BUILDER.RESPONSE_CHOICES' | translate }}</span>
              <mat-icon class="help-icon" matTooltip="{{ 'MESSAGE_SET_BUILDER.CHOICE_TAG_TOOLTIP' | translate }}">help_outline</mat-icon>
            </div>

            <div formArrayName="choices" class="choices-list">
              <div *ngFor="let choice of choicesFormArray.controls; let i = index; let first = first"
                   [formGroupName]="i"
                   class="choice-row">
                <span class="choice-number">{{ i + 1 }}</span>
                <mat-form-field appearance="outline" class="choice-text-field">
                  <mat-label>{{ 'MESSAGE_SET_BUILDER.CHOICE_PLACEHOLDER' | translate }}</mat-label>
                  <input matInput formControlName="choice_text" required />
                </mat-form-field>
                <mat-form-field appearance="outline" class="choice-tag-field">
                  <mat-label>{{ 'MESSAGE_SET_BUILDER.CHOICE_TAG_PLACEHOLDER' | translate }}</mat-label>
                  <input matInput formControlName="tag" />
                </mat-form-field>
                <button mat-icon-button color="warn" type="button" (click)="removeChoice(i)" *ngIf="!first">
                  <mat-icon>remove_circle_outline</mat-icon>
                </button>
              </div>

              <button mat-stroked-button type="button" class="add-choice-btn" (click)="addBlankChoice()" *ngIf="canAddChoice()">
                <mat-icon>add_circle_outline</mat-icon>
                {{ 'MESSAGE_SET_BUILDER.ADD_CHOICE' | translate }}
              </button>
            </div>
          </div>
        </form>
      </mat-card-content>

      <mat-divider></mat-divider>

      <mat-card-actions class="editor-actions">
        <button mat-stroked-button color="primary" type="button" (click)="addBlankMessage()">
          <mat-icon>note_add</mat-icon>
          {{ 'MESSAGE_SET_BUILDER.NEW_MESSAGE' | translate }}
        </button>
        <button
          mat-stroked-button
          color="warn"
          type="button"
          class="discard-btn"
          *ngIf="currentMessageForm.dirty"
          (click)="discardDraft()"
        >
          <mat-icon>undo</mat-icon>
          {{ 'MESSAGE_SET_BUILDER.DISCARD_DRAFT' | translate }}
        </button>
        <span class="actions-spacer"></span>
        <button mat-flat-button color="primary" type="button" (click)="addMessageToSet()" [disabled]="isAddDisabled()">
          <mat-icon>{{ isCurrentMessageInMessageSet() ? 'update' : 'playlist_add' }}</mat-icon>
          {{ (isCurrentMessageInMessageSet()
                ? 'MESSAGE_SET_BUILDER.UPDATE_MESSAGE'
                : 'MESSAGE_SET_BUILDER.ADD_TO_SET') | translate }}
        </button>
      </mat-card-actions>
    </mat-card>
  </div>
</div>
